import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MergeInviteCard } from '../../features/org-chart/restructure/components/merge-invite-card';
import { orgRestructureApi, orgRestructureKeys } from '../../features/org-chart/restructure/api';
import { Skeleton } from '../../components/ui/states';

/**
 * Trang nhận link gộp tổ chức — `/org-chart/merge-invite/$token`, APM-ORGMS-f (§3.7, §3.8
 * bước 2).
 *
 * `org-chart_` (gạch dưới cuối): URL nằm dưới `/org-chart` nhưng **không** lồng vào trang
 * Sơ đồ — trang ấy không có `<Outlet />`, và lồng vào thì trang nhận link không bao giờ vẽ.
 * Cùng lối đặt tên với `system_.ai.tsx`.
 *
 * Dưới `_app` có chủ đích: chỉ người đã đăng nhập mới nhận được (tư cách Admin kiểm ở tổ
 * chức họ chọn), và lớp gác của `_app` mang token theo qua trang đăng nhập (`?next=`).
 */
export const Route = createFileRoute('/_app/org-chart_/merge-invite/$token')({
  component: MergeInvitePage,
});

function MergeInvitePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();

  const preview = useQuery({
    queryKey: orgRestructureKeys.invite(token),
    queryFn: () => orgRestructureApi.preview(token),
    // Mọi kết cục xấu (hết hạn, đã có người nhận, đã đóng) là câu trả lời cuối cùng.
    retry: false,
  });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-medium">{t('orgChart.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('orgRestructure.accept.subtitle')}</p>
      </header>
      {preview.isPending ? (
        <Skeleton className="h-64 w-full max-w-lg" />
      ) : (
        <MergeInviteCard token={token} preview={preview.data ?? null} error={preview.error} />
      )}
    </div>
  );
}
